// Points path of the composer's Offer/Points radio (docs/06-ux-rules.md
// "Composer" / "Points input"; P6 D6). Validation is a positive integer up
// to a max that depends on the item's kind: a plain feedback rating routes
// through nexus' legacy points grant (capped at `POINTS_MAX_GRANT`), while
// RecoveryAI keeps the int32 ceiling (`GrantRewardModal.MAX_POINTS`, Q11 —
// no merchant-configured cap in the design).
import { Input } from '@thanx/uikit/input'
import { Text } from '@thanx/uikit/text'
import type { FeedbackItemKind } from 'api/feedbackInbox.types'
import { MAX_POINTS } from 'components/GrantRewardModal'
import { buildTranslate } from 'locales'
import React from 'react'
import { textUtils } from 'utilities/textUtils'

const t = buildTranslate('feedback_inbox.composer')
const validationT = buildTranslate('validations')

const POINTS_HELPER_ID = 'inbox-composer-points-helper'

// nexus caps plain-rating points at Api::V1::OrderRatings::POINTS_MAX_GRANT
// (1..100_000, a 400 on both the legacy resolve and the feedback_inbox
// ingress); RecoveryAI points are uncapped server-side, so they keep the
// int32 ceiling.
export const FEEDBACK_POINTS_MAX = 100_000

export function maxPointsFor(kind: FeedbackItemKind): number {
  return kind === 'feedback' ? FEEDBACK_POINTS_MAX : MAX_POINTS
}

// Exported for its own unit test — mirrors the repo's "pure derivation,
// tested directly" pattern (shouldShowComposer, deriveViewerCan, etc.).
export function isValidPoints(
  value: string,
  max: number = MAX_POINTS
): boolean {
  if (value.trim() === '') return false
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 && parsed <= max
}

function isOverMax(value: string, max: number): boolean {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > max
}

export interface PointsInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  max?: number
}

const PointsInput = ({
  value,
  onChange,
  disabled,
  max = MAX_POINTS,
}: PointsInputProps) => {
  const showError = value.trim() !== '' && !isValidPoints(value, max)
  const errorText = isOverMax(value, max)
    ? textUtils.initialCapital(
        textUtils.toSentence({
          array: [
            validationT('less_than_or_equal', {
              value: max.toLocaleString('en-US'),
            }),
          ],
        })
      )
    : t('points_error')

  return (
    <div>
      <Input
        value={value}
        onChange={onChange}
        placeholder={t('points_placeholder')}
        endEnhancer={t('points_suffix')}
        inputMode="numeric"
        disabled={disabled}
        // baseui's `error`, not uikit's `errored`: uikit declares `errored`
        // but never consumes it — it spreads through to baseui, which
        // doesn't know it either, while uikit's own `getInputStyle` reads
        // `$error`. `error` is also the only source baseui's allow-list
        // (base-input.js) reads `aria-invalid` from, so this carries both
        // the invalid state and the errored styling.
        error={showError}
        aria-describedby={POINTS_HELPER_ID}
        aria-label={t('points_input_aria')}
      />
      <div id={POINTS_HELPER_ID}>
        <Text.BodyText5 color={showError ? 'negative' : 'grey70'}>
          {showError ? errorText : t('points_helper')}
        </Text.BodyText5>
      </div>
    </div>
  )
}

export default PointsInput
